// Model configuration types

import { ReferenceMode } from 'containers/SubSidebar/AIVideoContent/consts'
import { TMediaItemType } from './media'

export interface IAIVideoModelOption {
  duration: number
  qualities: [
    {
      quality: number
      credit: number
      generationAvgDurationSeconds: number
    }
  ]
}

export interface IAIVideoModelDefaultValues {
  quality: number
  duration: number
}

export interface IFirstAndLastFrameRuleLeaf {
  field: 'videoDurationSeconds' | 'quality' | 'aspectRatio'
  value: string | number
}

export type TFirstAndLastFrameRule =
  | IFirstAndLastFrameRuleLeaf
  | { match: 'all' | 'any'; rules: TFirstAndLastFrameRule[] }

export type TFirstAndLastFrameSupport =
  | { isSupported: false }
  | { isSupported: true; conditions?: TFirstAndLastFrameRule }

export interface IMultipleReferenceMediasCapability {
  isSupported: boolean
  maxTotalCount?: number
  images?: { maxCount: number }
  videos?: { maxCount: number; maxLengthSeconds: number }
  audios?: { maxCount: number }
  limits?: { totalWhenMixed: number }
}

export interface ISupportedMediaMimeTypes {
  images?: string[]
  videos?: string[]
  audios?: string[]
}

export interface IFastTrackConfig {
  isSupported: boolean
  multiplier?: number
}

export type TAIVideoPromptConfig =
  | { isUnlimited: true }
  | { isUnlimited: false; maxCharacters: number }

export interface IAIVideoModelCapabilities {
  aspectRatios: string[] | null
  qualities: number[] | null
  isMediaResizingSupported: boolean
  isAudioGenerationSupported: boolean
  firstAndLastFrameSupport: TFirstAndLastFrameSupport
  multipleReferenceMedias?: IMultipleReferenceMediasCapability
  supportedMediaMimeTypes?: ISupportedMediaMimeTypes
  fastTrackConfig: IFastTrackConfig
  perUserConcurrency?: number
  promptConfig: TAIVideoPromptConfig
}

export interface IAIVideoModel {
  id: number
  name: string
  code: string
  model: string
  capabilities: IAIVideoModelCapabilities
  options: IAIVideoModelOption[]
  order: number
  isDefaultFor: string[]
  defaultValues: IAIVideoModelDefaultValues
  tariffPlans: number[]
  description: string
}

export enum ReferenceMediaImageType {
  FIRST_FRAME = 'firstFrame',
  LAST_FRAME = 'lastFrame',
  REFERENCE_IMAGE = 'referenceImage'
}

export enum ReferenceMediaAudioType {
  VOICE_OVER = 'voiceOver'
}

export interface IReferenceMediaDimensions {
  width: number
  height: number
}

export type TReferenceMode = ReferenceMode

export interface IReferenceMediaImage {
  type: ReferenceMediaImageType
  url: string
  dimensions?: IReferenceMediaDimensions
  nativeRatio?: string
}

export interface IReferenceMediaAudio {
  id: number
  // null when the generation used a cloned voice (`refAudio`) instead of a
  // catalog speaker — the two are mutually exclusive on the request.
  speakerId: number | null
  langCode: string
  text: string
  type: ReferenceMediaAudioType
  url: string
  // The cloned-voice audio URL the avatar was generated with, echoed back by
  // the backend. Present only on voice-clone generations; used to restore the
  // Voice clone card on the regeneration form.
  refAudio?: string
}

export interface IReferenceMediaItem {
  id: string
  type: ReferenceMediaImageType
  url: string
  isLoading: boolean
  dimensions?: IReferenceMediaDimensions
  nativeRatio?: string
}

export interface IReferenceMedias {
  frames?: Array<IReferenceMediaItem>
  images?: Array<IReferenceMediaItem>
}

export interface IApiReferenceMedias {
  images?: Array<IReferenceMediaImage>
  audios?: Array<IReferenceMediaAudio>
}

export interface IAIVideoGenerationParams {
  referenceMedias?: IReferenceMedias
  selectedModel: string | null
  duration: number
  aspectRatio: string | null
  quality: number
  prompt: string
  targetMediaItemId: string | null
  aiImageId: number | null
  isConsistencyEnabled: boolean
  smartPrompt: string
  isFastTrack: boolean
  refImageSelectedStyleId?: number
  sourceProjectId?: number
  parentProjectId?: number
  uploadedImagePath?: string
  // Pins the overlay preview to a specific asset's video (e.g. a chosen
  // version-history item) instead of the active scene's current media. Only
  // set while regenerating from a picked version; cleared on the next full
  // params load / generation.
  previewVideoUrl?: string
  // When regenerating from a chosen version-history item, the result is placed
  // as a NEW timeline clip (at the end) instead of replacing the active scene.
  // `targetMediaItemId` is still kept (it's the source for smart-edit), but the
  // generation handlers branch on this flag to add rather than replace. Only
  // set by `openAiVideoRegeneration`; cleared on the next full params load /
  // generation.
  addAsNewItem?: boolean
}

// API types
export interface IAIVideoGenerationRequestBody {
  id?: number // Id of Ai gen project, for enabled consistent generation
  referenceMedias?: IApiReferenceMedias
  prompt: string
  videoDurationSeconds: number
  videoModel: string
  aspectRatio?: string
  quality: number
  fastTrack?: boolean
  meta?: IAIVideoProjectMeta
}

export interface IAIVideoProjectMeta {
  reference?: IAIVideoProjectReference
  sourceProjectId?: number
  imageStyleId?: number
  parentProjectId?: number
  // Present only on avatar projects (avatars share the ai-video table/API). Its
  // presence is the discriminator used to type a selected clip as `aiAvatar`
  // rather than `aiVideo` — see `isAvatarVideoProject`.
  aiAvatarProjectId?: number
}

export interface IAIVideoProjectReference {
  id: number
  type: TMediaItemType
}

export enum AIVideoSocketMessageStatus {
  COMPLETED = 'completed',
  FAILED = 'failed',
  PROCESSING = 'processing'
}

export enum AIVideoSocketMessageState {
  ERROR = 'error',
  FINISHED = 'finished'
}

export interface IAIVideoSocketMessage {
  id: number
  referenceMedias: IApiReferenceMedias
  userId: number
  prompt: string
  status: AIVideoSocketMessageStatus
  state: AIVideoSocketMessageState
  aspectRatio: string
  quality: number
  createdAt: string
  externalVideoId: string
  type?: 'ai-video'
  updatedAt: string
  videoDurationSeconds: number
  videoSource: string
  videoModel: string
  videoUrl: string
  previewPath?: string
  width: number | null
  height: number | null
  failureReason: string
  meta?: IAIVideoProjectMeta
  /** Correlation key for generate-scene / edit-scene events */
  gen_ai_video_hash?: string
  /** Correlation key for /gen-ai/generate-video events */
  ai_video_generation_hash?: string
  generatedBy: string
}

export interface IAIVideoHistoryState {
  videos: IAIVideoProject[]
  isLoading: boolean
  isDataInitiallyLoaded: boolean
  totalCount: number
  rawOffset: number
  isScopedToCurrentProject: boolean
}

export interface IAIVideoListParams {
  limit?: number
  offset?: number
  projectIds?: number[]
  videoProjectId?: number
}

export interface IAIVideoProject {
  id: number | string
  referenceMedias: IApiReferenceMedias
  userId: number
  prompt: string
  status: AIVideoSocketMessageStatus | null
  aspectRatio: string | null
  quality: number
  createdAt?: string
  externalVideoId?: string | null
  type?: string
  updatedAt?: string
  videoDurationSeconds: number
  videoSource: string
  videoModel: string
  videoUrl: string
  previewPath?: string
  generatedBy: string
  width: number | null
  height: number | null
  meta?: IAIVideoProjectMeta
  imageUrl?: string
  thumbnail?: string | null
}

/**
 * Minimal AI-video fields the editor needs to place a generated video on the timeline
 * (the subset `convertAiVideoToBaseMediaItem` reads). Doubles as the contract for the
 * `v-editor-instructions` localStorage bridge. `id` must be the numeric AI-video id so
 * reference mode / versions resolve when the item is later regenerated.
 */
export type TAiVideoTimelineData = Pick<
  IAIVideoProject,
  | 'id'
  | 'width'
  | 'height'
  | 'videoUrl'
  | 'previewPath'
  | 'videoDurationSeconds'
  | 'thumbnail'
  | 'imageUrl'
  | 'meta'
>

/** POST /gen-ai/generate-video; genAiVideoHash is the pre-rename fallback */
export interface IAIVideoGenerationResponse {
  aiVideoGenerationHash?: string
  genAiVideoHash?: string
}

/** POST /gen-ai/generate-scene and /gen-ai/edit-scene */
export interface ISceneGenerationResponse {
  genAiVideoHash: string
}
